import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock, 
  faEye, 
  faEyeSlash, 
  faCheckCircle, 
  faTimesCircle, 
  faBuilding, 
  faEnvelope,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

function ResetPassword() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    organizationId: '',
    email: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [isOrganizationVerified, setIsOrganizationVerified] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const verifyOrganization = async () => {
    if (!formData.organizationId.trim() || !formData.email.trim()) {
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${BaseURL}Userauth/update-password/verify_organization/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: formData.organizationId.trim(),
          email: formData.email.trim()
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setOrganizationInfo(result.data);
        setIsOrganizationVerified(true);
        setErrorMessage('');
      } else {
        setErrorMessage(result.message || 'Invalid organization ID or email');
        setIsOrganizationVerified(false);
        setOrganizationInfo(null);
      }
    } catch (error) {
      console.error('Error verifying organization:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setIsOrganizationVerified(false);
      setOrganizationInfo(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isOrganizationVerified) {
      setErrorMessage('Please verify your organization ID and email first');
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (!isPasswordStrong) {
      setErrorMessage('Please meet all password requirements');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${BaseURL}Userauth/update-password/update_password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: formData.organizationId.trim(),
          email: formData.email.trim(),
          new_password: formData.password,
          confirm_password: formData.confirmPassword
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSuccess(true);
      } else {
        setErrorMessage(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualVerify = () => {
    if (!formData.organizationId.trim() || !formData.email.trim()) {
      setErrorMessage('Please enter both organization ID and email');
      return;
    }
    verifyOrganization();
  };

  const hasMinLength = formData.password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(formData.password);
  const hasLowerCase = /[a-z]/.test(formData.password);
  const hasNumber = /[0-9]/.test(formData.password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword !== '';
  const isPasswordStrong = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;

  const isFormValid = isOrganizationVerified && isPasswordStrong && passwordsMatch;

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '500px',
      padding: '40px',
      margin: '20px'
    },
    logo: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    logoH2: {
      color: '#2c3e50',
      fontSize: '1.8rem',
      marginBottom: '5px'
    },
    logoP: {
      color: '#2c3e50',
      opacity: 0.7,
      fontSize: '0.9rem'
    },
    form: {
      marginTop: '20px'
    },
    inputGroup: {
      marginBottom: '20px',
      textAlign: 'left'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#2c3e50',
      fontSize: '0.9rem'
    },
    inputField: {
      position: 'relative'
    },
    icon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2c3e50',
      opacity: 0.6,
      zIndex: 1
    },
    input: {
      width: '100%',
      padding: '12px 40px 12px 40px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    button: {
      width: '100%',
      padding: '12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: '#3498db',
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    footer: {
      marginTop: '30px',
      fontSize: '0.9rem',
      color: '#2c3e50',
      opacity: 0.7,
      textAlign: 'center'
    },
    errorMessage: {
      color: '#e74c3c',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#fde8e8',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    successMessage: {
      color: '#27ae60',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#e8f8f5',
      borderRadius: '4px',
      textAlign: 'center'
    },
    successBox: {
      textAlign: 'center'
    },
    successIcon: {
      marginBottom: '20px'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,.3)',
      borderRadius: '50%',
      borderTopColor: '#fff',
      animation: 'spin 1s ease-in-out infinite',
      marginRight: '8px',
      verticalAlign: 'middle'
    },
    verifyButton: {
      position: 'absolute',
      right: '5px',
      top: '50%',
      transform: 'translateY(-50%)',
      padding: '5px 12px',
      backgroundColor: '#3498db',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: '500',
      transition: 'all 0.3s'
    },
    verifyButtonDisabled: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    },
    eyeButton: {
      position: 'absolute',
      right: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#7f8c8d',
      padding: 0,
      fontSize: '1rem'
    },
    organizationInfoBox: {
      backgroundColor: '#e8f8f5',
      border: '1px solid #a3e0d5',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    organizationInfoText: {
      flex: 1
    },
    organizationName: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#1e8449',
      marginBottom: '4px'
    },
    organizationVerifiedText: {
      fontSize: '0.75rem',
      color: '#1e8449'
    },
    requirementsBox: {
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '20px',
      border: '1px solid #e9ecef'
    },
    requirementsTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '10px'
    },
    requirementsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px'
    },
    requirementItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.8rem'
    },
    requirementText: {
      color: '#6c757d'
    },
    requirementTextSuccess: {
      color: '#27ae60'
    },
    signinLink: {
      color: '#3498db',
      textDecoration: 'none',
      fontWeight: '600'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px'
    }
  };

  // Add global styles for animations
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
      
      input:focus {
        outline: none;
        border-color: #3498db !important;
        box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2) !important;
      }
      
      button:hover:not(:disabled) {
        background-color: #2980b9 !important;
      }
      
      .verify-btn:hover:not(:disabled) {
        background-color: #2980b9 !important;
      }
      
      .eye-btn:hover {
        color: #2c3e50 !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      margin: 0,
      padding: '20px'
    }}>
      <div style={styles.container}>
        <div style={styles.logo}>
          <h2 style={styles.logoH2}>CraneMonitor Pro</h2>
          <p style={styles.logoP}>Reset Your Password</p>
        </div>

        {isSuccess ? (
          // Success State
          <div style={styles.successBox}>
            <div style={styles.successIcon}>
              <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#27ae60" />
            </div>
            <h3 style={{ color: '#2c3e50', marginBottom: '10px' }}>Password Reset Successfully!</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '20px', fontSize: '0.9rem' }}>
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={styles.button}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              Sign In
            </button>
          </div>
        ) : (
          <>
            {errorMessage && (
              <div style={styles.errorMessage}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Organization Info */}
            {organizationInfo && isOrganizationVerified && (
              <div style={styles.organizationInfoBox}>
                <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" />
                <div style={styles.organizationInfoText}>
                  <div style={styles.organizationName}>{organizationInfo.company_name}</div>
                  <div style={styles.organizationVerifiedText}>Organization verified successfully</div>
                </div>
              </div>
            )}

            <form style={styles.form} onSubmit={handleSubmit}>
              {/* Organization ID Field */}
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="organizationId">Organization ID *</label>
                <div style={styles.inputField}>
                  <FontAwesomeIcon icon={faBuilding} style={styles.icon} />
                  <input
                    id="organizationId"
                    name="organizationId"
                    type="text"
                    style={styles.input}
                    value={formData.organizationId}
                    onChange={handleChange}
                    placeholder="Enter your organization ID"
                    required
                    disabled={isLoading || isVerifying || isOrganizationVerified}
                  />
                </div>
              </div>

              {/* Email Field */}
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="email">Email Address *</label>
                <div style={styles.inputField}>
                  <FontAwesomeIcon icon={faEnvelope} style={styles.icon} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    style={styles.input}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    required
                    disabled={isLoading || isVerifying || isOrganizationVerified}
                  />
                  {!isOrganizationVerified && (
                    <button
                      type="button"
                      onClick={handleManualVerify}
                      disabled={isVerifying || !formData.organizationId.trim() || !formData.email.trim()}
                      style={{
                        ...styles.verifyButton,
                        ...((isVerifying || !formData.organizationId.trim() || !formData.email.trim()) ? styles.verifyButtonDisabled : {})
                      }}
                      className="verify-btn"
                    >
                      {isVerifying ? 'Verifying...' : 'Verify'}
                    </button>
                  )}
                </div>
              </div>

              {/* Password Fields - Only show if organization is verified */}
              {isOrganizationVerified && (
                <>
                  <div style={styles.grid}>
                    {/* New Password Field */}
                    <div style={styles.inputGroup}>
                      <label style={styles.label} htmlFor="password">New Password *</label>
                      <div style={styles.inputField}>
                        <FontAwesomeIcon icon={faLock} style={styles.icon} />
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          style={styles.input}
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter new password"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={styles.eyeButton}
                          className="eye-btn"
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div style={styles.inputGroup}>
                      <label style={styles.label} htmlFor="confirmPassword">Confirm Password *</label>
                      <div style={styles.inputField}>
                        <FontAwesomeIcon icon={faLock} style={styles.icon} />
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          style={styles.input}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm new password"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={styles.eyeButton}
                          className="eye-btn"
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      {formData.confirmPassword && (
                        <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {passwordsMatch ? (
                            <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                          )}
                          <span style={{ fontSize: '0.75rem', color: passwordsMatch ? '#27ae60' : '#e74c3c' }}>
                            {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Password Requirements */}
                  <div style={styles.requirementsBox}>
                    <div style={styles.requirementsTitle}>Password Requirements:</div>
                    <div style={styles.requirementsGrid}>
                      <div style={styles.requirementItem}>
                        {hasMinLength ? (
                          <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                        )}
                        <span style={hasMinLength ? styles.requirementTextSuccess : styles.requirementText}>
                          At least 8 characters
                        </span>
                      </div>
                      <div style={styles.requirementItem}>
                        {hasUpperCase ? (
                          <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                        )}
                        <span style={hasUpperCase ? styles.requirementTextSuccess : styles.requirementText}>
                          One uppercase letter
                        </span>
                      </div>
                      <div style={styles.requirementItem}>
                        {hasLowerCase ? (
                          <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                        )}
                        <span style={hasLowerCase ? styles.requirementTextSuccess : styles.requirementText}>
                          One lowercase letter
                        </span>
                      </div>
                      <div style={styles.requirementItem}>
                        {hasNumber ? (
                          <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                        )}
                        <span style={hasNumber ? styles.requirementTextSuccess : styles.requirementText}>
                          One number
                        </span>
                      </div>
                      <div style={styles.requirementItem}>
                        {hasSpecialChar ? (
                          <FontAwesomeIcon icon={faCheckCircle} color="#27ae60" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTimesCircle} color="#e74c3c" size="sm" />
                        )}
                        <span style={hasSpecialChar ? styles.requirementTextSuccess : styles.requirementText}>
                          One special character
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    style={isLoading || !isFormValid ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
                    disabled={isLoading || !isFormValid}
                  >
                    {isLoading ? (
                      <>
                        <span style={styles.spinner}></span>
                        Resetting Password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </>
              )}
            </form>

            <div style={styles.footer}>
              <p>
                Remember your password?{' '}
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                  style={styles.signinLink}
                >
                  Sign in
                </a>
              </p>
              <p style={{ marginTop: '15px', fontSize: '0.8rem' }}>
                © 2026 CraneMonitor Pro. All rights reserved.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;